import { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  Container,
  CircularProgress,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
} from "@mui/material";

import Link from '@mui/material/Link';
import useSWRMutation from "swr/mutation";

import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useAuth } from "../../../context/AuthProvider";

import { PhoneNumber, validatePhoneNumber } from "../input/PhoneNumber";
import { Postcode, validatePostcode } from "../input/Postcode";
import { DateOfBirth, validateDateOfBirth } from "../input/DateOfBirth";
import { useAnalytics } from "../../../context/AnalyticsProvider";

export default function IdvOtpLoginCard() {

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const { setTag } = useAnalytics();
  const {
    formState,
    setFormState,
    formError,
    setFormError,
    createAlert,
    alertSeverityTypes
  } = useUtils();

  const {
    postIdvOtpAuth,
    setAuthState,
    authStates,
    setOtpUserId,
    setOtpSession,
  } = useAuth();

  const { trigger: idvTrigger, isMutating: idvIsMutating } = useSWRMutation("/auth/idv-otp", postIdvOtpAuth);

  const [ checked, setChecked ] = useState(false);
  const [ buttonDisabled, setButtonDisabled ] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setButtonDisabled(true);
    try {
      if (!validatePhoneNumber(formState.phoneNumber)) {
        throw new Error("Invalid phone number");
      }
      if (!validateDateOfBirth(formState.dateOfBirth)) {
        throw new Error("Invalid date of birth");
      }
      if (!validatePostcode(formState.postcode)) {
        throw new Error("Invalid postcode");
      }

      const request = {
        phoneNumber: `+44${formState.phoneNumber.substring(1)}`,
        dateOfBirth: formState.dateOfBirth,
        postcode: formState.postcode,
      };

      const response = await idvTrigger(request);
      console.info("post idv with otp auth response: ", response);

      if (response && response.challengeName === "CUSTOM_CHALLENGE") {
        setOtpUserId(response.username);
        setOtpSession(response.session);
        setAuthState(authStates.confirmOtp);
        setTag("login-idv-success");

      } else {
        const jsxMessage = (
          <Typography variant="body2" sx={{ color: '#99004C' }}>
            {clientConfig.layout.auth.login.loginErrorText} Please try again or you can call us on{' '}
            <Link href="tel:08003763333">0800 376 3333</Link> (or{' '}
            <Link href="tel:+442034930829">+44 203 493 0829</Link> if you're abroad).
          </Typography>
        );
        setFormError(jsxMessage);
        createAlert("The details you entered do not match with our records.", alertSeverityTypes.error);
        setTag("login-idv-failed");
      }
    } catch (error) {
      const isStringError = typeof error.message === 'string';
      const fallback = isStringError ? error.message : "Something went wrong";

      setFormError(
        <Typography variant="body2" sx={{ color: '#99004C' }}>
          * {fallback}
        </Typography>
      );
      createAlert(fallback, alertSeverityTypes.error);
      setButtonDisabled(false);
      setTag("login-idv-failed");
    }
  };


  useEffect(() => {
    const allValid =
      validatePhoneNumber(formState.phoneNumber) &&
      validateDateOfBirth(formState.dateOfBirth) &&
      validatePostcode(formState.postcode);

    setButtonDisabled(!allValid);
  }, [
    formState.phoneNumber,
    formState.dateOfBirth,
    formState.postcode,
  ]);

  return (
    clientConfig && (
      <Card
        sx={{
          maxWidth: { md: "80%" },
          margin: "0px auto",
          borderRadius: "0",
          position: "relative",
          bottom: { xs: "auto", md: "300px" },
          boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
        }}
      >
        <Container
          sx={{
            marginTop: "25px",
          }}
        >
          <Typography
            component='h1'
            variant='h5'
            fontWeight={"bold"}
            marginBottom={1}
          >
            {clientConfig.layout.auth.login.headerText}
          </Typography>
          <Typography component='h2' variant='p'>
            {clientConfig.layout.auth.login.subHeaderText}
          </Typography>
        </Container>

        <Box
          component='form'
          onSubmit={handleSubmit}
          noValidate
          sx={{ margin: "0px" }}
        >
          <Stack
            sx={{
              paddingX: 1,
            }}
            direction={{
              xs: "column",
              md: "row",
            }}
          >
            <CardContent
              sx={{
                textAlign: "left",
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Phone number section */}
              <Box id="phoneNumber">
                <PhoneNumber
                  autoFocus
                  label={`Mobile number`}
                  inputLabel={`Enter your mobile number`}
                  formState={formState}
                  setFormState={setFormState}
                  setFormError={setFormError}
                />
              </Box>
              {/* End of phone number section */}

              {/* Postcode input */}
              <Box id="postcode" sx={{ mt: { xs: 4, md: 2 } }}>
                <Postcode
                  label="Postcode"
                  inputLabel="Enter your postcode"
                  formState={formState}
                  setFormState={setFormState}
                  setFormError={setFormError}
                />
              </Box>
              {/* End of postcode input */}
            </CardContent>

            <CardContent
              sx={{
                textAlign: "left",
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DateOfBirth
                label="Date of birth"
                formState={formState}
                setFormState={setFormState}
                setFormError={setFormError}
              />
            </CardContent>
          </Stack>
          {clientConfig.layout.auth.login.confirmationToggle &&
            <Container
              sx={{
                marginBottom: "10px",
              }}
            >
              <Checkbox
                checked={checked}
                onChange={() => {
                  setChecked(!checked);
                }}
                inputProps={{ "aria-label": "controlled" }}
                sx={{
                  marginRight: { xs: "8px", sm: 0 },
                  marginBottom: { xs: "8px", sm: 0 },
                }}
              />
              <Typography
                component='h2'
                variant='p'
                sx={{
                  display: "inline-block",
                  verticalAlign: {
                    xs: "middle",
                    sm: "middle",
                    md: "baseline",
                    lg: "baseline",
                  },
                  maxWidth: { xs: "88%" },
                  marginLeft: { xs: "0", sm: "8px" },
                }}
              >
                Tick this box to agree to confirm you've read & accepted our{" "}
                <Link
                  to={clientConfig?.layout?.links?.terms}
                  style={{ color: "#007CCB" }}
                  target='_blank'
                  rel="noopener noreferrer"
                >
                  Terms and Conditions
                </Link>{" "}
                &{" "}
                <Link
                  to={clientConfig?.layout?.links?.privacy}
                  style={{ color: "#007CCB" }}
                  target='_blank'
                  rel="noopener noreferrer"
                >
                  Privacy Notice
                </Link>
              </Typography>
            </Container>
          }
          <Container
            sx={{
              mt: 4,
              mb: 5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'flex-start',
              gap: 2,
            }}
          >
            <Button
              type='submit'
              variant='contained'
              sx={{
                mb: 2,
                marginLeft: "1%",
                borderRadius: clientConfig.layout.buttonBorderRadius,
                width: { xs: "60%", md: "15%" },
                backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                color: clientConfig.layout.primaryButtonColour,
                "&:hover": {
                  backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                  color: clientConfig.layout.hoverPrimaryButtonColour,
                }
              }}
              disabled={
                !(
                  (!clientConfig.layout.auth.login.confirmationToggle || checked) &&
                  !buttonDisabled
                )
              }

            >
              Login
            </Button>
            {idvIsMutating && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
            <Typography
              sx={{
                width: { md: '60%' },
                color: '#99004C',
                mt: 1,
                ml: 1,
                mb: { xs: 2 },
                fontSize: { xs: '13px', sm: '13px' },
              }}
              color='error'
            >
              {formError}
            </Typography>
          </Container>
        </Box>

        </Card>

    )
  );
}
